import { api } from './client';

export interface WeeklyPoint {
  label: string;
  value: number;
}
export interface BankDist {
  key: string;
  label: string;
  count: number;
  pct: number;
}
export interface DashboardStats {
  totalCollected: number;
  approvedCount: number;
  failedCount: number;
  dealerCount: number;
  newDealers: number;
  awaitingReview: number;
  enabledBanks: number;
  successRate: number;
  collectedDeltaPct: number | null;
  approvedDeltaPct: number | null;
  weekly: WeeklyPoint[];
  bankDistribution: BankDist[];
  recent: Array<{
    order_id: string;
    amount: string;
    bank_key: string | null;
    status: string;
    review_status: string;
    created_at: string;
    user_company: string | null;
    user_name: string | null;
  }>;
}

export interface Dealer {
  id: number;
  full_name: string | null;
  company_name: string | null;
  email: string;
  phone: string | null;
  tax_number: string | null;
  address: string | null;
  is_active: boolean;
  created_at: string;
}

export interface AdminPayment {
  id: number;
  order_id: string;
  amount: string;
  installment: number;
  bank_key: string | null;
  status: 'pending' | 'approved' | 'failed';
  review_status: 'awaiting' | 'approved' | 'rejected';
  created_at: string;
  user_name: string | null;
  user_company: string | null;
}

export interface Staff {
  id: number;
  full_name: string | null;
  email: string;
  role: 'admin' | 'manager';
  is_active: boolean;
  created_at: string;
}

export interface AdminBank {
  key: string;
  label: string;
  enabled: boolean;
  sortOrder: number;
  environment: 'test' | 'production';
  securityType: string;
  config: Record<string, unknown>;
}

export interface Contract {
  key: string;
  title: string;
  body: string | null;
  updated_at: string;
}

interface Paged {
  total: number;
  page: number;
  pageSize: number;
}

export interface StaffNotification {
  id: number;
  type: string;
  title: string;
  body: string | null;
  ref_id: number | null;
  read_at: string | null;
  created_at: string;
}

export const adminApi = {
  stats: () => api.get<DashboardStats>('/admin/stats'),
  dealers: (q: string, page: number) =>
    api.get<{ dealers: Dealer[] } & Paged>(`/admin/dealers?q=${encodeURIComponent(q)}&page=${page}`),
  createDealer: (body: Record<string, unknown>) => api.post<{ dealer: Dealer }>('/admin/dealers', body),
  updateDealer: (id: number, body: Record<string, unknown>) =>
    api.patch<{ dealer: Dealer; emailPending?: boolean }>(`/admin/dealers/${id}`, body),
  deleteDealer: (id: number) => api.del<{ ok: boolean }>(`/admin/dealers/${id}`),
  resetPassword: (id: number, newPassword?: string) =>
    api.post<{ generated: boolean; password?: string }>(
      `/admin/dealers/${id}/reset-password`,
      newPassword ? { newPassword } : {},
    ),
  staff: (q: string, page: number) =>
    api.get<{ staff: Staff[] } & Paged>(`/admin/staff?q=${encodeURIComponent(q)}&page=${page}`),
  createStaff: (body: Record<string, unknown>) => api.post<{ staff: Staff }>('/admin/staff', body),
  deleteStaff: (id: number) => api.del<{ ok: boolean }>(`/admin/staff/${id}`),
  updateStaff: (id: number, body: Record<string, unknown>) =>
    api.patch<{ staff: Staff }>(`/admin/staff/${id}`, body),
  resetStaffPassword: (id: number, newPassword?: string) =>
    api.post<{ generated: boolean; password?: string }>(
      `/admin/staff/${id}/reset-password`,
      newPassword ? { newPassword } : {},
    ),
  payments: (params: string) =>
    api.get<{ payments: AdminPayment[] } & Paged>(`/admin/payments?${params}`),
  deletePayment: (id: number) => api.del<{ ok: boolean }>(`/admin/payments/${id}`),
  banks: () => api.get<{ banks: AdminBank[] }>('/admin/banks'),
  updateBank: (key: string, body: Record<string, unknown>) => api.patch<{ ok: boolean }>(`/admin/banks/${key}`, body),
  contracts: () => api.get<{ contracts: Contract[] }>('/admin/contracts'),
  saveContract: (key: string, title: string, body: string) =>
    api.put<{ ok: boolean }>(`/admin/contracts/${key}`, { title, body }),
  getSetting: (key: string) => api.get<{ value: Record<string, unknown> }>(`/admin/settings/${key}`),
  saveSetting: (key: string, value: Record<string, unknown>) => api.put<{ ok: boolean }>(`/admin/settings/${key}`, { value }),
  testSmtp: (to?: string) =>
    api.post<{ ok: boolean; error?: string; live?: boolean; to?: string }>('/admin/settings/smtp/test', to ? { to } : {}),
  notifications: () =>
    api.get<{ items: StaffNotification[]; unread: number }>('/admin/notifications'),
  markNotificationsRead: () =>
    api.post<{ ok: boolean }>('/admin/notifications/read', {}),
  getDbConfig: () =>
    api.get<{ config: Record<string, unknown>; source: 'file' | 'env' }>('/admin/database'),
  testDbConfig: (body: Record<string, unknown>) =>
    api.post<{ ok: boolean; error?: string }>('/admin/database/test', body),
  saveDbConfig: (body: Record<string, unknown>) =>
    api.put<{ ok: boolean; restartRequired: boolean }>('/admin/database', body),
};
